import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

/**
 * Multi-Select Dropdown Component
 * A proper dropdown with tags for selected items
 */
const MultiSelectDropdown = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Select...",
  labelKey = "name",
  valueKey = "id",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSelected = (option) => {
    return value.includes(option[valueKey]);
  };

  const toggleOption = (option) => {
    const optionValue = option[valueKey];
    if (isSelected(option)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const removeOption = (e, optionValue) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };

  const getOptionLabel = (optionValue) => {
    const option = options.find(o => o[valueKey] === optionValue);
    return option ? option[labelKey] : optionValue;
  };

  const selectedOptions = options.filter(o => value.includes(o[valueKey]));

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main button/trigger */}
      <div
        className={`
          min-h-[42px] w-full border border-border rounded-md px-3 py-2 
          bg-background cursor-pointer flex flex-wrap items-center gap-1.5
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'}
          ${isOpen ? 'ring-2 ring-primary/20 border-primary' : ''}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedOptions.length > 0 ? (
          <>
            {selectedOptions.map((option) => (
              <span
                key={option[valueKey]}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-sm rounded-md"
              >
                {option[labelKey]}
                {Number(option.status) === 1 && (
                  <span className="text-green-600 text-xs">(Active)</span>
                )}
                <button
                  type="button"
                  onClick={(e) => removeOption(e, option[valueKey])}
                  className="hover:bg-primary/20 rounded-full p-0.5 ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown 
          className={`w-4 h-4 ml-auto text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {options.length > 0 ? (
            options.map((option) => (
              <div
                key={option[valueKey]}
                className={`
                  px-3 py-2 cursor-pointer flex items-center justify-between
                  ${isSelected(option) ? 'bg-primary/10 text-primary' : 'hover:bg-muted dark:hover:bg-gray-800'}
                `}
                onClick={() => toggleOption(option)}
              >
                <span className="flex items-center gap-2">
                  {option[labelKey]}
                  {Number(option.status) === 1 ? (
                    <span className="text-green-600 text-xs font-medium">(Active)</span>
                  ) : (
                    <span className="text-red-500 text-xs font-medium">(Inactive)</span>
                  )}
                </span>
                {isSelected(option) && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-muted-foreground text-sm">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
